import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import Avatar from 'Shared/components/Avatar/Avatar';
import { editorStateForEditorComponent } from 'Shared/utils/draftJsHelpers';
import { dateMMMDDYYYY } from 'Shared/utils/DateTime.utils';
import { FeedbackCommentType } from 'Shared/constants/types';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';
import VideoPlayer from 'Shared/components/VideoPlayer/VideoPlayer';
import { destroyFeedbackComment, viewFeedbackComment } from 'Shared/actions/FeedbackComments.actions';
import { useFocusOnSeen } from 'Shared/utils/hooks/use-focus-on-seen';
import { useDidUpdateEffect } from 'Shared/utils/hooks';

import OptionsButtons from '../../OptionsButtons/OptionsButtons';
import './FeedbackCommentItem.scss';

type Props = {
  comment: FeedbackCommentType;
  currentUserId: number;
  onEditCommentClick: Function;
  sectionRef: {
    current: HTMLElement;
  };
};

function FeedbackCommentItem({
  comment: { uuid, body, user, createdAt, video, isEdited, discardedAt, viewerIds },
  currentUserId,
  onEditCommentClick,
  sectionRef,
}: Props) {
  const dispatch = useDispatch();
  const [editorState, setEditorState] = useState(editorStateForEditorComponent(body));

  const commentIsViewed = useMemo(() => viewerIds.some(item => item === currentUserId), [currentUserId, viewerIds]);

  const handleEditorChange = useCallback(state => setEditorState(state), []);

  const handleEditClick = useCallback(() => onEditCommentClick(uuid), [uuid, onEditCommentClick]);

  const handleDeleteClick = useCallback(() => {
    if (window.confirm('Are you sure you want to delete this comment?')) dispatch(destroyFeedbackComment(uuid));
  }, [dispatch, uuid]);

  const commenterName = useMemo(() => (currentUserId === user?.id ? 'You' : user.fullName || user.full_name), []);

  const itemRef = useRef(null);

  const handleViewItem = useCallback(() => {
    if (!commentIsViewed) dispatch(viewFeedbackComment(uuid));
  }, [commentIsViewed, dispatch, uuid]);

  useDidUpdateEffect(() => {
    setEditorState(editorStateForEditorComponent(body));
  }, [body]);

  useFocusOnSeen({
    sectionRef,
    itemRef,
    onFocus: handleViewItem,
    offsetBottom: 300,
    offsetTop: 0,
  });

  return (
    <div className="feedback-comment-item d-flex flex-column mb-3" ref={itemRef}>
      <div className="d-flex align-items-center mb-2">
        <Avatar avatar={user.avatar} height={28} />
        <div className="feedback-comment-item-title-author text-muted mx-2">
          {`${commenterName} left a comment - ${dateMMMDDYYYY(createdAt)}`}
        </div>
        {currentUserId === user.id && !discardedAt && (
          <OptionsButtons onEditClick={handleEditClick} onDeleteClick={handleDeleteClick} />
        )}
      </div>
      <div className="feedback-comment-item-content d-flex p-2">
        {!discardedAt ? (
          <>
            <div className="w-100">
              {video ? (
                <VideoPlayer
                  videoID={video.id}
                  sources={[video.mp4Url, video.url]}
                  previewImageURL={video.previewImageUrl}
                  transcription={video.transcription}
                  audioOnly={video.audioOnly}
                />
              ) : (
                <DraftEditor
                  readOnly
                  isInlineAttachments
                  imageAttachmentFullWidth
                  editorState={editorState}
                  onChange={handleEditorChange}
                />
              )}
            </div>
            {isEdited && (
              <div className="feedback-comment-item-content--edited align-self-center text-muted ml-3">Edited</div>
            )}
          </>
        ) : (
          <div className="feedback-comment-item-content--deleted">
            {user.firstName || user.email} deleted this comment
          </div>
        )}
      </div>
    </div>
  );
}

export default FeedbackCommentItem;
